import type { LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * Pestañas de una pantalla, enlazadas a la URL.
 *
 * Cada pestaña es una ruta de verdad (React Router `NavLink`), no un estado interno: así se
 * puede compartir el enlace de una pestaña concreta, funciona el botón "atrás" y el título
 * del header sigue siendo el de la sección (el shell resuelve el padre por prefijo de ruta).
 *
 * En móvil la fila de pestañas se desplaza en horizontal en vez de partirse en varias líneas.
 */

export interface TabItem {
  label: string;
  to: string;
  icon?: LucideIcon;
}

export function Tabs({ items, className }: { items: TabItem[]; className?: string }) {
  return (
    <nav
      aria-label="Secciones de la pantalla"
      className={cn('scroll-thin -mb-px overflow-x-auto border-b border-line', className)}
    >
      <ul className="flex min-w-max items-center gap-1">
        {items.map((item) => {
          const Icon = item.icon;

          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-body font-medium transition-colors',
                    isActive
                      ? 'border-brand-600 text-brand-700'
                      : 'border-transparent text-ink-muted hover:border-line-strong hover:text-ink-soft',
                  )
                }
              >
                {Icon ? <Icon size={16} /> : null}
                {item.label}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
